import Image from 'next/image'
import { Badge } from '@/components/ui/badge'
import type { GptImage2Prompt } from '@/content/marketing/gpt-image-2-prompts/prompts'
import { CopyPromptButton } from './copy-prompt-button'
import { TryInSokuButton } from './try-in-soku-button'

const ASPECT_CLASS: Record<GptImage2Prompt['aspectRatio'], string> = {
  '1:1': 'aspect-square',
  '4:3': 'aspect-[4/3]',
  '3:4': 'aspect-[3/4]',
  '16:9': 'aspect-[16/9]',
  '9:16': 'aspect-[9/16]',
}

const CATEGORY_LABEL: Record<string, string> = {
  'product-hero': 'Product Hero',
  lifestyle: 'Lifestyle',
  'ooh-mockup': 'OOH Mockup',
  'social-ad': 'Social Ad',
  'type-poster': 'Type Poster',
  'flat-lay': 'Flat Lay',
  packaging: 'Packaging',
  'brand-collab': 'Brand Collab',
  editorial: 'Editorial',
}

interface PromptCardProps {
  prompt: GptImage2Prompt
  /** All renders use png today; kept here so future formats stay easy. */
  imageExt: 'jpg' | 'png' | 'webp'
}

export function PromptCard({ prompt, imageExt }: PromptCardProps) {
  const src = `https://cdn.soku.ai/marketing/prompts/gpt-image-2/${prompt.slug}.${imageExt}`

  return (
    <article className="mb-6 break-inside-avoid overflow-hidden rounded-2xl border border-border bg-background transition-shadow hover:shadow-lg">
      <div
        className={`relative w-full overflow-hidden bg-muted ${ASPECT_CLASS[prompt.aspectRatio]}`}
      >
        <Image
          src={src}
          alt={prompt.title}
          fill
          className="object-cover"
          sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 33vw"
        />
        <div className="absolute top-3 left-3">
          <Badge variant="secondary" className="bg-background/85 backdrop-blur-sm">
            {CATEGORY_LABEL[prompt.category] ?? prompt.category}
          </Badge>
        </div>
        {prompt.imageAttribution && (
          <a
            href={prompt.imageAttribution.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="absolute right-3 bottom-3 inline-flex items-center rounded-md bg-background/85 px-2 py-0.5 text-[10px] font-medium tracking-wide text-foreground/75 backdrop-blur-sm transition-colors hover:text-foreground"
          >
            {prompt.imageAttribution.label}
          </a>
        )}
      </div>

      <div className="flex flex-col gap-3 p-5">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            {prompt.title}
          </h3>
          <p className="mt-1.5 text-xs text-muted-foreground">{prompt.useCase}</p>
        </div>

        <p className="text-sm leading-relaxed whitespace-pre-line text-foreground/75">
          {prompt.prompt}
        </p>

        {prompt.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {prompt.tags.map(tag => (
              <span
                key={tag}
                className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium tracking-wide text-muted-foreground uppercase"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-2 flex items-stretch gap-2">
          <CopyPromptButton
            prompt={prompt.prompt}
            slug={prompt.slug}
            category={prompt.category}
          />
          <TryInSokuButton slug={prompt.slug} category={prompt.category} />
        </div>
      </div>
    </article>
  )
}
